// applicationCounterService.js
//
// Provides per-application sequential numbering for defects and feature requests.
// Each application has its own counter; numbers start at 1 and are immutable.
//
// Firestore data model:
//
//   applicationCounters/{applicationName}
//     nextDefectNumber: number   // next # to assign to a new defect
//     nextFeatureNumber: number  // next # to assign to a new feature request
//     defectsBackfilled: bool    // whether existing defects have been numbered
//     featuresBackfilled: bool   // whether existing features have been numbered
//
// Numbering is preserved when items are deleted (we don't decrement). New items
// always get the current next* value, then we increment.

import {
  doc, runTransaction, collection, query, where,
  getDocs, orderBy, writeBatch, getDoc
} from 'firebase/firestore';
import { db } from '../firebase';

const COUNTERS = 'applicationCounters';

/**
 * Reserve the next defect number for the given application.
 * Returns the assigned number (1, 2, 3, ...).
 */
export async function nextDefectNumber(applicationName) {
  if (!applicationName) {
    throw new Error('applicationName is required for sequential numbering');
  }
  return await runTransaction(db, async (tx) => {
    const ref = doc(db, COUNTERS, applicationName);
    const snap = await tx.get(ref);
    const current = snap.exists() ? (snap.data().nextDefectNumber || 1) : 1;
    tx.set(ref, { nextDefectNumber: current + 1 }, { merge: true });
    return current;
  });
}

/**
 * Reserve the next feature-request number for the given application.
 */
export async function nextFeatureNumber(applicationName) {
  if (!applicationName) {
    throw new Error('applicationName is required for sequential numbering');
  }
  return await runTransaction(db, async (tx) => {
    const ref = doc(db, COUNTERS, applicationName);
    const snap = await tx.get(ref);
    const current = snap.exists() ? (snap.data().nextFeatureNumber || 1) : 1;
    tx.set(ref, { nextFeatureNumber: current + 1 }, { merge: true });
    return current;
  });
}

/**
 * One-time backfill: assign displayNumber to all existing defects per application,
 * sorted oldest-first by createdAt. Idempotent — checks the counter doc's
 * `defectsBackfilled` flag and exits if already done.
 *
 * Safe to call on every app load; will no-op after the first successful run.
 */
export async function backfillDefectNumbers() {
  // Group defects by application
  const allDefects = await getDocs(
    query(collection(db, 'defects'), orderBy('createdAt', 'asc'))
  );
  const byApp = new Map();
  allDefects.docs.forEach(d => {
    const data = d.data();
    const app = data.applicationName || '__no_app__';
    if (!byApp.has(app)) byApp.set(app, []);
    byApp.get(app).push({ id: d.id, ...data });
  });

  for (const [app, defects] of byApp.entries()) {
    if (app === '__no_app__') continue; // skip orphans; don't number them

    const counterRef = doc(db, COUNTERS, app);
    const counterSnap = await getDoc(counterRef);
    if (counterSnap.exists() && counterSnap.data().defectsBackfilled) {
      continue;
    }

    // Find the highest existing displayNumber so we don't double-assign on partial backfills
    const numbered = defects.filter(d => typeof d.displayNumber === 'number');
    const unnumbered = defects.filter(d => typeof d.displayNumber !== 'number');
    const maxAssigned = numbered.reduce((m, d) => Math.max(m, d.displayNumber), 0);

    // Batch the writes
    const batch = writeBatch(db);
    let next = maxAssigned + 1;
    unnumbered.forEach(d => {
      batch.update(doc(db, 'defects', d.id), { displayNumber: next });
      next++;
    });
    batch.set(counterRef, {
      nextDefectNumber: next,
      defectsBackfilled: true
    }, { merge: true });
    await batch.commit();
  }
}

/**
 * One-time backfill for feature requests, mirror of backfillDefectNumbers.
 */
export async function backfillFeatureNumbers() {
  const allFeatures = await getDocs(
    query(collection(db, 'featureRequests'), orderBy('createdAt', 'asc'))
  );
  const byApp = new Map();
  allFeatures.docs.forEach(d => {
    const data = d.data();
    const app = data.applicationName || '__no_app__';
    if (!byApp.has(app)) byApp.set(app, []);
    byApp.get(app).push({ id: d.id, ...data });
  });

  for (const [app, features] of byApp.entries()) {
    if (app === '__no_app__') continue;

    const counterRef = doc(db, COUNTERS, app);
    const counterSnap = await getDoc(counterRef);
    if (counterSnap.exists() && counterSnap.data().featuresBackfilled) {
      continue;
    }

    const numbered = features.filter(f => typeof f.displayNumber === 'number');
    const unnumbered = features.filter(f => typeof f.displayNumber !== 'number');
    const maxAssigned = numbered.reduce((m, f) => Math.max(m, f.displayNumber), 0);

    const batch = writeBatch(db);
    let next = maxAssigned + 1;
    unnumbered.forEach(f => {
      batch.update(doc(db, 'featureRequests', f.id), { displayNumber: next });
      next++;
    });
    batch.set(counterRef, {
      nextFeatureNumber: next,
      featuresBackfilled: true
    }, { merge: true });
    await batch.commit();
  }
}

// ── Build 24: counters for new tracker apps ────────────────────────────────
//
// These follow the same per-application pattern as defects/features.
// SOP numbering is different — see SOP section below.

/** Reserve the next incident number for the given application. */
export async function nextIncidentNumber(applicationName) {
  if (!applicationName) {
    throw new Error('applicationName is required for sequential numbering');
  }
  return await runTransaction(db, async (tx) => {
    const ref = doc(db, COUNTERS, applicationName);
    const snap = await tx.get(ref);
    const current = snap.exists() ? (snap.data().nextIncidentNumber || 1) : 1;
    tx.set(ref, { nextIncidentNumber: current + 1 }, { merge: true });
    return current;
  });
}

/** Reserve the next injury number for the given application. */
export async function nextInjuryNumber(applicationName) {
  if (!applicationName) {
    throw new Error('applicationName is required for sequential numbering');
  }
  return await runTransaction(db, async (tx) => {
    const ref = doc(db, COUNTERS, applicationName);
    const snap = await tx.get(ref);
    const current = snap.exists() ? (snap.data().nextInjuryNumber || 1) : 1;
    tx.set(ref, { nextInjuryNumber: current + 1 }, { merge: true });
    return current;
  });
}

/** Reserve the next maintenance-request number for the given application. */
export async function nextMaintenanceNumber(applicationName) {
  if (!applicationName) {
    throw new Error('applicationName is required for sequential numbering');
  }
  return await runTransaction(db, async (tx) => {
    const ref = doc(db, COUNTERS, applicationName);
    const snap = await tx.get(ref);
    const current = snap.exists() ? (snap.data().nextMaintenanceNumber || 1) : 1;
    tx.set(ref, { nextMaintenanceNumber: current + 1 }, { merge: true });
    return current;
  });
}

// ── SOP numbering ──────────────────────────────────────────────────────────
//
// Format: {ORG}-{DEPT}-SOP-{NNN}-v{M}
//   ORG  = "WC" (hardcoded, future: configurable in settings)
//   DEPT = QA | OPS | ENG | MFG | REG | SAFETY (admin-selected on creation)
//   SOP  = type literal (future: WI, TR, etc.)
//   NNN  = zero-padded 3-digit sequential per (ORG, DEPT, TYPE)
//   M    = version, increments on each published edit (drafts don't bump)
//
// Counter doc: applicationCounters/SOP_{DEPT}, field nextNumber
//
// We use a separate counter-doc namespace (SOP_ prefix) so we don't collide
// with applicationName counters.

const SOP_ORG = 'WC';
const SOP_TYPE = 'SOP';

export function formatSopNumber({ dept, number, version }) {
  const paddedNumber = String(number).padStart(3, '0');
  return `${SOP_ORG}-${dept}-${SOP_TYPE}-${paddedNumber}-v${version}`;
}

/** Reserve the next SOP number for a department. Returns just the integer. */
export async function nextSopNumber(dept) {
  if (!dept) {
    throw new Error('dept is required for SOP numbering');
  }
  return await runTransaction(db, async (tx) => {
    const ref = doc(db, COUNTERS, `SOP_${dept}`);
    const snap = await tx.get(ref);
    const current = snap.exists() ? (snap.data().nextNumber || 1) : 1;
    tx.set(ref, { nextNumber: current + 1, dept, type: SOP_TYPE }, { merge: true });
    return current;
  });
}

export const SOP_ORG_CODE = SOP_ORG;
export const SOP_DEPARTMENTS = ['QA', 'OPS', 'ENG', 'MFG', 'REG', 'SAFETY'];
